import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { deleteTag, listTags, renameTag, type AdminTag } from '../../api/admin';
import { Alert, Button, DataTable, EmptyState, Input, LoadingState } from '../ui';

/**
 * The words authors used, most used first.
 *
 * Respelling a tag changes how it reads and nothing else — the slug is what
 * stories are attached by. Deleting one takes it off every story that carries
 * it, which is why the confirmation says how many that is.
 */
export function AdminTags() {
  const [tags, setTags] = useState<AdminTag[] | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async (search: string) => {
    try {
      const page = await listTags(search);
      setTags(page.results);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setTags([]);
    }
  }, []);

  useEffect(() => {
    void load(query);
  }, [load, query]);

  const run = async (slug: string, action: () => Promise<unknown>) => {
    setWorking(slug);
    setError(null);
    try {
      await action();
      await load(query);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking('');
    }
  };

  const rename = (tag: AdminTag) => {
    const next = window.prompt(`How should #${tag.slug} be spelled?`, tag.name);
    if (next === null || !next.trim()) {
      return;
    }
    void run(tag.slug, () => renameTag(tag.slug, next.trim()));
  };

  const remove = (tag: AdminTag) => {
    const carried = tag.story_count === 1 ? '1 story' : `${tag.story_count} stories`;
    const reason = window.prompt(`Take #${tag.name} off ${carried}. Why?`);
    if (reason === null) {
      return;
    }
    void run(tag.slug, () => deleteTag(tag.slug, reason.trim()));
  };

  return (
    <>
      {error && (
        <Alert tone="warning" title="That did not work">
          {error.message}
        </Alert>
      )}

      <Input
        label="Find a tag"
        placeholder="Name or slug"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {tags === null ? (
        <LoadingState label="Reading the tags…" />
      ) : (
        <DataTable
          caption="Tags in use"
          rows={tags}
          rowKey={(tag) => tag.id}
          empty={<EmptyState title="No tags match that" description="Try a shorter search." />}
          columns={[
            {
              key: 'tag',
              header: 'Tag',
              render: (tag) => (
                <>
                  <strong>{tag.name}</strong>
                  <span className="sc-admin__muted"> #{tag.slug}</span>
                </>
              ),
            },
            { key: 'stories', header: 'Stories', numeric: true, render: (tag) => tag.story_count },
            {
              key: 'actions',
              header: 'Actions',
              render: (tag) => (
                <span className="sc-admin__inline">
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={working === tag.slug}
                    onClick={() => rename(tag)}
                  >
                    Rename
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={working === tag.slug}
                    onClick={() => remove(tag)}
                  >
                    Delete
                  </Button>
                </span>
              ),
            },
          ]}
        />
      )}
    </>
  );
}
